/**
 * QZ Tray Service
 * Handles all QZ Tray communication for printing ZPL labels and PDFs
 *
 * This service manages:
 * - WebSocket connection to QZ Tray desktop application
 * - Printer discovery and selection
 * - ZPL printing to thermal printers (e.g., Zebra GX420d)
 * - PDF printing to office printers
 * - User printer preferences (localStorage)
 */

import qz from "qz-tray";
import axios from "axios";

// ============================================================================
// LOGGING UTILITIES
// ============================================================================

const LOG_PREFIX = "[QZ Tray Service]";
const QZ_DEBUG = typeof localStorage !== "undefined" && localStorage.getItem("qz_debug") === "true";
const LOG_STYLES = {
    info: "color: #2196F3; font-weight: bold",
    success: "color: #4CAF50; font-weight: bold",
    error: "color: #F44336; font-weight: bold",
    warn: "color: #FF9800; font-weight: bold",
    debug: "color: #9E9E9E",
};

const log = {
    info: (...args) => console.log(`%c${LOG_PREFIX} [INFO]`, LOG_STYLES.info, ...args),
    success: (...args) => console.log(`%c${LOG_PREFIX} [SUCCESS]`, LOG_STYLES.success, ...args),
    error: (...args) => console.error(`%c${LOG_PREFIX} [ERROR]`, LOG_STYLES.error, ...args),
    warn: (...args) => console.warn(`%c${LOG_PREFIX} [WARN]`, LOG_STYLES.warn, ...args),
    debug: (...args) => QZ_DEBUG && console.log(`%c${LOG_PREFIX} [DEBUG]`, LOG_STYLES.debug, ...args),
};

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

let qzConnection = {
    isConnected: false,
    isConnecting: false,
    connectionAttempts: 0,
    lastError: null,
    pendingConnectionPromise: null, // Store pending Promise for deduplication
    intentionalDisconnect: false, // Set true in disconnectFromQZ() to suppress auto-reconnect
    autoReconnectAttempts: 0, // Tracks consecutive auto-reconnect failures
    reconnectPromise: null, // Promise that resolves when the scheduled auto-reconnect completes
    disconnectedProactively: false, // True when we proactively disconnected (tab hidden or idle timeout)
    hasEverConnected: false, // True once connectToQZ succeeds; never reset. Used by ensureConnected to know if a reconnect is worth attempting.
};

let visibilityHandlerAttached = false;
let visibilityDisconnectTimer = null;
let idleDisconnectTimer = null;
let activeQzOperations = 0;
const IDLE_DISCONNECT_MS = 1 * 60 * 1000; // 1 min — well before QZ Tray's hardcoded 5-min server timeout

let printerCache = {
    allPrinters: [],
    lastFetched: null,
    cacheExpiryMs: 30000, // 30 seconds
};

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEYS = {
    ZPL_PRINTER: "qz_default_zpl_printer",
    PDF_PRINTER: "qz_default_pdf_printer",
    AUTO_CONNECT: "qz_auto_connect_enabled",
};

const QZ_ERRORS = {
    NOT_INSTALLED: "QZ Tray is not installed or not running",
    CONNECTION_FAILED: "Failed to connect to QZ Tray",
    PRINTER_NOT_FOUND: "Printer not found",
    PRINT_FAILED: "Print job failed",
    NO_PRINTERS: "No printers found",
};

// Common error messages
const ERROR_DETAILS_MSG = "Error details:";

// ============================================================================
// QZ TRAY SIGNING (for silent printing without popups)
// ============================================================================

/** Backend endpoint that returns the QZ Tray public certificate (preferred over static file). */
const PRINT_CERT_ENDPOINT = "/WebUI/Shipping/SmartParcel/PrintCert";

/**
 * Normalize PrintCert API response to PEM/text or null if unusable.
 * @param {*} data - axios response.data (JSON object or raw string)
 * @returns {string|null}
 */
function extractCertificateFromPrintCertResponse(data) {
    if (data == null) {
        return null;
    }
    if (typeof data === "string") {
        const trimmed = data.trim();
        return trimmed.length ? trimmed : null;
    }
    if (typeof data === "object") {
        var nested =
            data.Certificate ||
            data.certificate ||
            data.Cert ||
            data.cert ||
            data.Value ||
            data.value ||
            data.Data ||
            data.data;
        if (typeof nested === "string" && nested.trim()) {
            return nested.trim();
        }
    }
    return null;
}

/**
 * GET SmartParcel PrintCert; throws if empty or not usable.
 * @returns {Promise<string>}
 */
function fetchCertificateFromPrintCert() {
    return axios.get(PRINT_CERT_ENDPOINT).then(function (response) {
        var certText = extractCertificateFromPrintCertResponse(response.data);
        if (!certText) {
            throw new Error("PrintCert returned empty or unrecognized certificate payload");
        }
        return certText;
    });
}

// Tracks whether a real certificate is loaded; when false, we skip backend signing
// to avoid "Private key not configured" errors and EXCEPTION modals.
let signingAvailable = false;

// Cache the certificate text after first successful fetch so reconnects
// don't depend on a network request (which can fail in throttled tabs).
let cachedCertificate = null;

// Ensures setupQzSigning() only runs once (promises persist across reconnects).
let signingSetupDone = false;

/**
 * Setup QZ Tray certificate and signature promises for silent printing.
 * MUST be called exactly ONCE (guarded by signingSetupDone flag).
 * QZ Tray reuses the registered promise functions on every connect/reconnect,
 * so re-registering them is unnecessary and can cause race conditions.
 *
 * Features:
 * - Loads digital certificate from GET SmartParcel/PrintCert when available; falls back to
 *   {origin}/assets/signing/digital-certificate.txt (root path, not under /smartui)
 * - Caches certificate in memory after first fetch (no network needed on reconnect)
 * - Calls backend API only when a real certificate is loaded (skips when placeholder)
 * - Graceful fallback: no PrintSign request when signing unavailable, so no EXCEPTION modal
 * - No breaking changes: works with or without backend endpoint
 */
function setupQzSigning() {
    if (signingSetupDone) {
        log.debug("QZ Tray signing already configured, skipping");
        return;
    }
    signingSetupDone = true;

    log.info("Configuring QZ Tray signing for silent printing...");

    try {
        // Set signature algorithm (SHA512 for QZ Tray 2.1+)
        qz.security.setSignatureAlgorithm("SHA512");
        log.debug("Signature algorithm set to SHA512");

        // Step 1: Setup certificate promise (loads public certificate)
        let certificateUrl = "";
        if (typeof window !== "undefined") {
            certificateUrl = new URL("assets/signing/digital-certificate.txt", window.location.href).href;
        }
        qz.security.setCertificatePromise(function (resolve, reject) {
            // Return cached certificate immediately (no network needed on reconnect)
            if (cachedCertificate) {
                log.debug("✓ Using cached certificate (no network fetch)");
                resolve(cachedCertificate);
                return;
            }

            function applyCertificateText(certificateText) {
                // Check if it's a placeholder file
                if (certificateText.includes("Placeholder") || certificateText.includes("placeholder")) {
                    log.warn("⚠️ Certificate file is placeholder - signing not configured");
                    log.warn("   This is OK for local development (POC mode)");
                    log.warn("   For production: replace with actual digital-certificate.txt");
                    signingAvailable = false;
                    cachedCertificate = certificateText;
                    resolve(certificateText);
                } else {
                    log.debug("✓ Certificate loaded successfully");
                    signingAvailable = true;
                    cachedCertificate = certificateText;
                    resolve(certificateText);
                }
            }

            log.debug("Loading QZ Tray certificate from SmartParcel PrintCert (fallback: static file)");

            fetchCertificateFromPrintCert()
                .then(function (certificateText) {
                    log.debug("✓ Certificate received from SmartParcel PrintCert");
                    applyCertificateText(certificateText);
                })
                .catch(function (printCertError) {
                    console.error(
                        LOG_PREFIX +
                            " SmartParcel PrintCert failed or returned empty certificate; using static digital-certificate.txt.",
                        printCertError && printCertError.message ? printCertError.message : printCertError
                    );
                    return fetch(certificateUrl, {
                        cache: "no-store",
                        headers: { "Content-Type": "text/plain" },
                    })
                        .then(function (response) {
                            if (response.ok) {
                                return response.text();
                            } else {
                                throw new Error("Certificate not found (HTTP " + response.status + ")");
                            }
                        })
                        .then(function (certificateText) {
                            applyCertificateText(certificateText);
                        });
                })
                .catch(function (error) {
                    log.warn("⚠️ Certificate load failed:", error.message);
                    log.warn("   Continuing in POC mode (unsigned) - security popups will appear");
                    log.warn("   To enable silent printing: add digital-certificate.txt to /public/assets/signing/");
                    signingAvailable = false;
                    reject(error);
                });
        });

        // Step 2: Setup signature promise (calls backend only when signing is configured)
        qz.security.setSignaturePromise(function (toSign) {
            log.debug("toSign:", toSign);
            return function (resolve, reject) {
                // Skip backend call when using placeholder cert — avoids "Private key not configured"
                // and prevents the app from showing an EXCEPTION modal for failed PrintSign.
                if (!signingAvailable) {
                    log.warn("⚠️ Skipping backend signing (placeholder or missing certificate)");
                    log.warn("   QZ Tray will use POC/unsigned mode — security popup may appear");
                    reject(new Error("Signing not configured (use real digital-certificate.txt for silent printing)"));
                    return;
                }

                log.debug("Requesting signature from backend API...");

                // Call backend endpoint to sign the message
                axios
                    .post("/WebUI/Shipping/SmartParcel/PrintSign?uri=/properties/printsign", {
                        request: toSign,
                    })
                    .then(function (response) {
                        if (response.data && response.data.Signature) {
                            log.debug("✓ Signature received from backend");
                            resolve(response.data.Signature);
                        } else {
                            throw new Error("Invalid signature response format");
                        }
                    })
                    .catch(function (error) {
                        log.warn("⚠️ Backend signing failed:", error.message);
                        log.warn("   Continuing in POC mode (unsigned) - security popup will appear");
                        log.warn("   This is expected for local/feature branch development");
                        reject(error);
                    });
            };
        });

        log.success("✓ QZ Tray signing configured (will attempt backend signing)");
        log.info("   If backend unavailable, will fall back to POC mode with popups");
    } catch (error) {
        // If any error in setup, log but don't break connection
        signingSetupDone = false; // Allow retry on next connect if setup itself threw
        log.warn("⚠️ Error setting up QZ Tray signing:", error.message);
        log.warn("   Continuing in POC mode (unsigned) - security popups will appear");
    }
}

// Register certificate and signature promises once at module load.
// QZ Tray reuses these across all connect/reconnect cycles.
setupQzSigning();

// ============================================================================
// CONNECTION MANAGEMENT
// ============================================================================

/**
 * Connect to QZ Tray desktop application
 * @param {Object} options - Connection options
 * @param {number} options.retries - Number of retry attempts (default: 3)
 * @param {number} options.retryDelay - Delay between retries in ms (default: 1000)
 * @param {number} options.connectionTimeout - Max ms to wait for connection before failing (default: 10000). Prevents UI from hanging when QZ Tray is not installed.
 * @returns {Promise<boolean>} - True if connected successfully
 */
export async function connectToQZ(options = {}) {
    const { retries = 3, retryDelay = 1000, connectionTimeout = 10000 } = options;

    log.info("Attempting to connect to QZ Tray...");
    log.debug("Connection options:", { retries, retryDelay, connectionTimeout });

    // CRITICAL: Check for in-progress connection FIRST before any other checks
    if (qzConnection.isConnecting && qzConnection.pendingConnectionPromise) {
        log.debug("Connection already in progress, waiting for existing attempt...");
        // Wait for the existing connection attempt instead of starting a new one
        return qzConnection.pendingConnectionPromise;
    }

    // Check if already connected (both internal state and WebSocket state)
    const wsActive = qz?.websocket?.isActive?.();
    if (qzConnection.isConnected || wsActive) {
        if (wsActive && !qzConnection.isConnected) {
            qzConnection.isConnected = true;
            qzConnection.isConnecting = false;
        }
        log.debug("Already connected to QZ Tray");
        return true;
    }

    qzConnection.isConnecting = true;
    qzConnection.intentionalDisconnect = false;
    qzConnection.autoReconnectAttempts = 0;

    // Create and store the connection Promise for deduplication
    // Any other caller will await this same Promise
    qzConnection.pendingConnectionPromise = (async () => {
        let timeoutId;

        try {
            // Signing is configured once at module load (setupQzSigning).
            // QZ Tray reuses the registered promise functions across reconnects.

            // Check if QZ Tray is running (with timeout so UI doesn't hang when QZ is not installed)
            log.debug("Checking QZ Tray WebSocket availability...");

            const connectPromise = qz.websocket.connect({
                retries: retries,
                delay: retryDelay,
                keepAlive: 5, // Ping every 5s — must be well below Jetty's idle timeout (~30s) to avoid race conditions
            });

            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(
                        new Error(
                            "Connection timed out. QZ Tray is not running or not installed. Please download and install QZ Tray from https://qz.io/download/"
                        )
                    );
                }, connectionTimeout);
            });

            await Promise.race([connectPromise, timeoutPromise]);
            clearTimeout(timeoutId); // connection won; prevent timeout from firing and causing unhandled rejection

            qzConnection.isConnected = true;
            qzConnection.isConnecting = false;
            qzConnection.pendingConnectionPromise = null;
            qzConnection.lastError = null;
            qzConnection.connectionAttempts++;
            qzConnection.hasEverConnected = true;

            const version = await qz.api.getVersion();
            log.success("✓ Connected to QZ Tray successfully!");
            log.debug("QZ Tray version:", version);

            // Set up disconnect handler + visibility-based background management
            setupDisconnectHandler();
            setupVisibilityHandler();
            resetIdleTimer();

            return true;
        } catch (error) {
            clearTimeout(timeoutId);

            qzConnection.isConnected = false;
            qzConnection.isConnecting = false;
            qzConnection.pendingConnectionPromise = null;
            qzConnection.lastError = error;
            qzConnection.connectionAttempts++;

            log.error("✗ Failed to connect to QZ Tray");
            log.error(ERROR_DETAILS_MSG, error);
            log.error("Is QZ Tray installed and running?");
            log.info("Download QZ Tray from: https://qz.io/download/");

            throw new Error(QZ_ERRORS.CONNECTION_FAILED + ": " + error.message);
        }
    })();

    return qzConnection.pendingConnectionPromise;
}

/**
 * Disconnect from QZ Tray
 * @param {boolean} force - If true, attempts disconnect even if isConnected flag is false
 */
export async function disconnectFromQZ(force = false) {
    log.debug("Disconnecting from QZ Tray...", force ? "(forced)" : "");

    clearIdleTimer();

    // Signal to the closed-callback that this disconnect is intentional
    qzConnection.intentionalDisconnect = true;

    if (!qzConnection.isConnected && !force) {
        log.debug("Not connected, nothing to disconnect");
        qzConnection.isConnecting = false;
        qzConnection.pendingConnectionPromise = null;
        qzConnection.reconnectPromise = null;
        return;
    }

    try {
        if (qz && qz.websocket) {
            await qz.websocket.disconnect();
        }
        qzConnection.isConnected = false;
        qzConnection.isConnecting = false;
        qzConnection.pendingConnectionPromise = null;
        qzConnection.reconnectPromise = null;
        log.success("✓ Disconnected from QZ Tray");
    } catch (error) {
        if (force || (error?.message || "").includes("not connected")) {
            log.debug("Disconnect error (expected):", error?.message || error);
        } else {
            log.error("Error disconnecting from QZ Tray:", error);
        }
        qzConnection.isConnected = false;
        qzConnection.isConnecting = false;
        qzConnection.pendingConnectionPromise = null;
        qzConnection.reconnectPromise = null;
    }
}

const AUTO_RECONNECT_MAX_ATTEMPTS = 20;
const AUTO_RECONNECT_BASE_DELAY_MS = 200;
const AUTO_RECONNECT_MAX_DELAY_MS = 30000;

/**
 * Set up handler for unexpected disconnections.
 * Uses qz.websocket.setClosedCallbacks() (QZ Tray 2.1+) to detect when the
 * WebSocket closes and automatically reconnect after a short delay.
 *
 * Reconnect uses exponential backoff: 200ms, 400ms, 800ms, ... capped at 30s.
 * The reconnectPromise is set immediately so that ensureConnected() callers
 * can await the in-progress reconnect without starting a duplicate one.
 *
 * Guards:
 *  - Skips reconnect when disconnectFromQZ() was called intentionally.
 *  - Skips reconnect when a connection attempt is already in progress.
 *  - Caps consecutive auto-reconnect failures at AUTO_RECONNECT_MAX_ATTEMPTS.
 */
function setupDisconnectHandler() {
    log.debug("Setting up disconnect handler with auto-reconnect via setClosedCallbacks");

    qz.websocket.setClosedCallbacks(function (closeEvent) {
        log.warn("WebSocket closed. Code:", closeEvent.code, "Reason:", closeEvent.reason || "(none)");

        qzConnection.isConnected = false;
        qzConnection.isConnecting = false;
        qzConnection.pendingConnectionPromise = null;

        // Update global state so the UI reflects the disconnection
        updateGlobalConnectionState(false);

        if (qzConnection.intentionalDisconnect) {
            log.debug("Disconnect was intentional, skipping auto-reconnect");
            qzConnection.reconnectPromise = null;
            return;
        }

        if (qzConnection.autoReconnectAttempts >= AUTO_RECONNECT_MAX_ATTEMPTS) {
            log.warn(
                "Auto-reconnect: max attempts (" +
                    AUTO_RECONNECT_MAX_ATTEMPTS +
                    ") reached, giving up. Will reconnect on next print attempt."
            );
            qzConnection.reconnectPromise = null;
            return;
        }

        var attempt = qzConnection.autoReconnectAttempts + 1;
        var delay = Math.min(AUTO_RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt - 1), AUTO_RECONNECT_MAX_DELAY_MS);
        log.info(
            "Auto-reconnect: attempt " + attempt + " of " + AUTO_RECONNECT_MAX_ATTEMPTS + " in " + delay + "ms..."
        );

        // Set reconnectPromise IMMEDIATELY so ensureConnected() can await it
        qzConnection.reconnectPromise = new Promise(function (resolveReconnect) {
            setTimeout(function () {
                // Re-check guards after the delay
                if (qzConnection.intentionalDisconnect || qzConnection.isConnected || qzConnection.isConnecting) {
                    log.debug("Auto-reconnect: skipped (state changed during delay)");
                    resolveReconnect(qzConnection.isConnected);
                    return;
                }

                qzConnection.autoReconnectAttempts = attempt;

                connectToQZ({ retries: 2, retryDelay: 1000 })
                    .then(function () {
                        log.success("Auto-reconnect: successfully reconnected! (attempt " + attempt + ")");
                        qzConnection.autoReconnectAttempts = 0;
                        qzConnection.reconnectPromise = null;
                        updateGlobalConnectionState(true);
                        resolveReconnect(true);
                    })
                    .catch(function (error) {
                        log.error("Auto-reconnect: attempt " + attempt + " failed:", error?.message || error);
                        resolveReconnect(false);
                        // The closed callback will fire again from the failed connection,
                        // which will set a new reconnectPromise for the next attempt.
                    });
            }, delay);
        });
    });

    log.debug("Disconnect handler with auto-reconnect configured");
}

/**
 * Reset (or start) the idle-disconnect timer.
 * After IDLE_DISCONNECT_MS of no print/printer-settings activity, we cleanly
 * disconnect before QZ Tray's server-side 5-minute timeout can fire a desktop
 * notification.  The next print will reconnect in ~200ms via ensureConnected.
 */
function resetIdleTimer() {
    clearIdleTimer();
    if (!qzConnection.isConnected) return;
    idleDisconnectTimer = setTimeout(function () {
        if (!qzConnection.isConnected) return;
        log.info(
            "No print activity for 3 min — disconnecting to prevent timeout " +
                "notification. Will reconnect on next print."
        );
        qzConnection.disconnectedProactively = true;
        disconnectFromQZ();
    }, IDLE_DISCONNECT_MS);
}

function clearIdleTimer() {
    if (idleDisconnectTimer) {
        clearTimeout(idleDisconnectTimer);
        idleDisconnectTimer = null;
    }
}

/**
 * Prevent idle disconnects while QZ is actively servicing a request.
 * This keeps the socket open during slow printer discovery on machines
 * with many installed printers.
 * @param {Function} operation
 * @returns {Promise<*>}
 */
async function withActiveQzOperation(operation) {
    activeQzOperations += 1;
    clearIdleTimer();

    try {
        return await operation();
    } finally {
        activeQzOperations = Math.max(0, activeQzOperations - 1);
        if (activeQzOperations === 0) {
            resetIdleTimer();
        }
    }
}

/**
 * Build a lightweight printer summary from a printer name and optional metadata.
 * Used for fast dropdown rendering without waiting on full printer details.
 * @param {string} printerName
 * @param {string} driver
 * @param {Object|null} details
 * @returns {Object}
 */
function buildPrinterSummary(printerName, driver = "Unknown", details = null) {
    const normalizedName = (printerName || "").toLowerCase();
    const normalizedDriver = (driver || "Unknown").toLowerCase();

    const isZebraThermal =
        normalizedName.includes("zebra") ||
        normalizedName.includes("zpl") ||
        normalizedName.includes("ztc") ||
        normalizedDriver.includes("zebra") ||
        normalizedDriver.includes("zpl") ||
        normalizedDriver.includes("zdesigner");

    const isThermal =
        isZebraThermal ||
        normalizedName.includes("thermal") ||
        normalizedName.includes("dymo") ||
        normalizedName.includes("brother ql") ||
        normalizedName.includes("rollo") ||
        normalizedName.includes("bixolon") ||
        normalizedDriver.includes("thermal");

    return {
        name: printerName,
        driver: driver || "Unknown",
        isThermal,
        isZebraThermal,
        supportsPdf: true,
        supportsZpl: isZebraThermal,
        details,
    };
}

/**
 * Proactively disconnect when the browser tab is hidden and reconnect when
 * it becomes visible again.
 *
 * Why: Chrome throttles setInterval in background tabs (to once/minute after
 * ~5 min).  The qz-tray.js keepAlive pings rely on setInterval, so they stop
 * arriving in time and QZ Tray's Jetty server fires an idle-timeout close —
 * which pops up an annoying desktop notification every ~60 s.
 *
 * By disconnecting intentionally (clean WebSocket close) before Jetty can
 * time out, QZ Tray never shows the notification.  When the tab returns we
 * reconnect instantly (~200 ms) so the user never notices.
 *
 * A 2-second debounce avoids unnecessary churn on quick alt-tab bounces.
 */
function setupVisibilityHandler() {
    if (typeof document === "undefined" || visibilityHandlerAttached) return;

    document.addEventListener("visibilitychange", function () {
        if (document.hidden) {
            visibilityDisconnectTimer = setTimeout(function () {
                if (!document.hidden || !qzConnection.isConnected) return;
                log.debug("Tab hidden — disconnecting to prevent idle-timeout notification");
                qzConnection.disconnectedProactively = true;
                disconnectFromQZ();
            }, 2000);
        } else {
            // Tab is visible again — cancel pending disconnect if it hasn't fired
            if (visibilityDisconnectTimer) {
                clearTimeout(visibilityDisconnectTimer);
                visibilityDisconnectTimer = null;
            }

            if (qzConnection.disconnectedProactively) {
                qzConnection.disconnectedProactively = false;
                qzConnection.intentionalDisconnect = false;
                log.debug("Tab visible — reconnecting after background disconnect");
                connectToQZ({ retries: 2, retryDelay: 1000, connectionTimeout: 5000 }).catch(function (err) {
                    log.warn("Reconnect on tab focus failed:", err?.message || err);
                });
            }
        }
    });

    visibilityHandlerAttached = true;
    log.debug("Visibility-based disconnect/reconnect handler configured");
}

/**
 * Update global connection state via dynamic import to avoid circular deps.
 * (qz-tray.service.js intentionally does not statically import global_services.js)
 * @param {boolean} connected
 */
function updateGlobalConnectionState(connected) {
    import("../global_services")
        .then(function (GlobalService) {
            GlobalService.setQzTrayConnected(connected);
        })
        .catch(function () {
            // global_services may not be available in all contexts
        });
}

/**
 * Check if connected to QZ Tray
 * @returns {boolean}
 */
export function isConnected() {
    const connected = qzConnection.isConnected && qz?.websocket?.isActive?.() === true;
    log.debug("Connection status check:", connected);
    return connected;
}

/**
 * Whether QZ Tray connected at least once this session.
 * Used to decide if a reconnect attempt is worth trying.
 * @returns {boolean}
 */
export function hasConnectedBefore() {
    return qzConnection.hasEverConnected;
}

/**
 * Get connection status details
 * @returns {Object}
 */
export function getConnectionStatus() {
    const status = {
        isConnected: qzConnection.isConnected,
        isConnecting: qzConnection.isConnecting,
        connectionAttempts: qzConnection.connectionAttempts,
        lastError: qzConnection.lastError ? qzConnection.lastError.message : null,
    };
    log.debug("Connection status:", status);
    return status;
}

/**
 * Ensure QZ Tray is connected before printing. Handles three scenarios:
 *
 * 1. Already connected → returns true instantly.
 * 2. A reconnect/connection attempt is in flight → waits up to timeoutMs.
 * 3. Not connected, nothing in flight, but QZ Tray was connected earlier this
 *    session (hasEverConnected) → attempts a fast reconnect (retries: 0).
 * 4. QZ Tray was NEVER connected this session → returns false instantly
 *    (user doesn't have QZ Tray, no point waiting).
 *
 * This means Browser Print-only users experience ZERO delay.
 *
 * @param {number} timeoutMs - Max time to wait for reconnect (default 3000ms)
 * @returns {Promise<boolean>} true if connected after waiting
 */
export async function ensureConnected(timeoutMs = 3000) {
    if (isConnected()) {
        log.debug("ensureConnected: already connected");
        return true;
    }

    // Determine which promise to await (active connect attempt or scheduled reconnect)
    const waitPromise = qzConnection.pendingConnectionPromise || qzConnection.reconnectPromise;

    if (waitPromise) {
        log.info("ensureConnected: reconnect in progress, waiting up to " + timeoutMs + "ms...");

        try {
            await Promise.race([
                waitPromise,
                new Promise(function (_, reject) {
                    setTimeout(function () {
                        reject(new Error("ensureConnected timeout"));
                    }, timeoutMs);
                }),
            ]);
        } catch (e) {
            log.warn("ensureConnected: " + (e.message || "wait failed"));
        }

        const nowConnected = isConnected();
        log.info("ensureConnected: done waiting, connected=" + nowConnected);
        return nowConnected;
    }

    // Nothing in flight — attempt a fast reconnect if QZ Tray was seen this session
    if (!qzConnection.hasEverConnected) {
        log.debug("ensureConnected: QZ Tray never connected this session, returning false");
        return false;
    }

    log.info("ensureConnected: QZ Tray was connected before, attempting fast reconnect...");
    try {
        await connectToQZ({ retries: 0, connectionTimeout: timeoutMs });
    } catch (e) {
        log.warn("ensureConnected: fast reconnect failed — " + (e.message || "unknown"));
    }

    const nowConnected = isConnected();
    log.info("ensureConnected: fast reconnect result, connected=" + nowConnected);
    return nowConnected;
}

// ============================================================================
// PRINTER DISCOVERY & MANAGEMENT
// ============================================================================

/**
 * Find all available printers with detailed information
 * Uses QZ Tray's qz.printers.details() (no args) to get ALL printer info in a SINGLE call
 * This minimizes authorization prompts (1 prompt instead of N prompts)
 * @param {boolean} useCache - Use cached results if available (default: true)
 * @returns {Promise<Array<Object>>} - Array of printer objects with name and capabilities
 */
export async function findPrintersWithDetails(useCache = true) {
    log.debug("Finding all available printers with driver detection...");

    // Ensure connected FIRST
    if (!isConnected()) {
        log.warn("Not connected to QZ Tray, attempting to connect...");
        await connectToQZ();
    }

    try {
        log.debug("Querying QZ Tray for all printer details in single call...");
        const allPrinterDetails = await withActiveQzOperation(() => qz.printers.details());

        // Categorize each printer based on driver and name
        const printersWithDetails = allPrinterDetails.map((details) =>
            buildPrinterSummary(details.name, details.driver || "Unknown", details)
        );

        log.success(`✓ Found ${printersWithDetails.length} printer(s)`);
        return printersWithDetails;
    } catch (error) {
        log.error("✗ Failed to find printers with details:", error);
        log.warn("Falling back to basic printer list without driver info...");

        // Fallback: if details query fails entirely, get basic printer list
        try {
            const printers = await withActiveQzOperation(() => qz.printers.find());
            return printers.map((printerName) => buildPrinterSummary(printerName, "Unknown (fallback mode)"));
        } catch (fallbackError) {
            log.error("✗ Fallback also failed:", fallbackError);
            throw new Error(QZ_ERRORS.NO_PRINTERS + ": " + fallbackError.message);
        }
    }
}

/**
 * Find printers with lightweight capability hints derived from names only.
 * This is fast enough for large printer dropdowns because it avoids
 * MediaTray/detail lookups for every installed printer.
 * @param {boolean} useCache - Use cached results if available (default: true)
 * @returns {Promise<Array<Object>>}
 */
export async function findPrinterSummaries(useCache = true) {
    const printers = await findPrinters(useCache);
    return printers.map((printerName) => buildPrinterSummary(printerName));
}

/**
 * Find all available printers (names only)
 * @param {boolean} useCache - Use cached results if available (default: true)
 * @returns {Promise<Array<string>>} - Array of printer names
 */
export async function findPrinters(useCache = true) {
    log.debug("Finding all available printers...");

    const now = Date.now();
    if (
        useCache &&
        printerCache.allPrinters.length > 0 &&
        printerCache.lastFetched &&
        now - printerCache.lastFetched < printerCache.cacheExpiryMs
    ) {
        log.debug("Using cached printer list");
        return printerCache.allPrinters;
    }

    // Ensure connected
    if (!isConnected()) {
        log.warn("Not connected to QZ Tray, attempting to connect...");
        await connectToQZ();
    }

    try {
        log.debug("Querying QZ Tray for printers...");
        const printers = await withActiveQzOperation(() => qz.printers.find());

        printerCache.allPrinters = printers;
        printerCache.lastFetched = now;

        log.success(`✓ Found ${printers.length} printer(s)`);
        return printers;
    } catch (error) {
        log.error("✗ Failed to find printers:", error);
        throw new Error(QZ_ERRORS.NO_PRINTERS + ": " + error.message);
    }
}

/**
 * Find a specific printer by name (supports partial matching)
 * @param {string} printerName - Full or partial printer name
 * @returns {Promise<string|null>} - Full printer name or null if not found
 */
export async function findSpecificPrinter(printerName) {
    log.debug("Searching for specific printer:", printerName);

    if (!printerName) {
        log.warn("No printer name provided");
        return null;
    }

    try {
        const printers = await findPrinters();

        // Try exact match first
        const exactMatch = printers.find((p) => p.toLowerCase() === printerName.toLowerCase());

        if (exactMatch) return exactMatch;

        const partialMatch = printers.find((p) => p.toLowerCase().includes(printerName.toLowerCase()));
        if (partialMatch) return partialMatch;

        log.warn("✗ Printer not found:", printerName);
        return null;
    } catch (error) {
        log.error("Error searching for printer:", error);
        return null;
    }
}

// ============================================================================
// PRINTER PREFERENCES
// ============================================================================

/**
 * Get default ZPL printer (for thermal labels)
 * @returns {string|null}
 */
export function getDefaultZplPrinter() {
    const saved = localStorage.getItem(STORAGE_KEYS.ZPL_PRINTER);
    log.debug("Default ZPL printer:", saved || "None set");
    return saved;
}

/**
 * Set default ZPL printer
 * @param {string} printerName
 */
export function setDefaultZplPrinter(printerName) {
    log.debug("Setting default ZPL printer:", printerName);
    localStorage.setItem(STORAGE_KEYS.ZPL_PRINTER, printerName);
    log.debug("✓ Default ZPL printer saved");
}

/**
 * Get default PDF printer (for documents)
 * @returns {string|null}
 */
export function getDefaultPdfPrinter() {
    const saved = localStorage.getItem(STORAGE_KEYS.PDF_PRINTER);
    log.debug("Default PDF printer:", saved || "None set");
    return saved;
}

/**
 * Set default PDF printer
 * @param {string} printerName
 */
export function setDefaultPdfPrinter(printerName) {
    log.debug("Setting default PDF printer:", printerName);
    localStorage.setItem(STORAGE_KEYS.PDF_PRINTER, printerName);
    log.debug("✓ Default PDF printer saved");
}

/**
 * Clear default ZPL printer
 */
export function clearDefaultZplPrinter() {
    log.debug("Clearing default ZPL printer...");
    localStorage.removeItem(STORAGE_KEYS.ZPL_PRINTER);
    log.debug("✓ Default ZPL printer cleared");
}

/**
 * Clear default PDF printer
 */
export function clearDefaultPdfPrinter() {
    log.debug("Clearing default PDF printer...");
    localStorage.removeItem(STORAGE_KEYS.PDF_PRINTER);
    log.debug("✓ Default PDF printer cleared");
}

/**
 * Clear all printer preferences
 */
export function clearPrinterPreferences() {
    log.debug("Clearing all printer preferences...");
    localStorage.removeItem(STORAGE_KEYS.ZPL_PRINTER);
    localStorage.removeItem(STORAGE_KEYS.PDF_PRINTER);
    log.debug("✓ Printer preferences cleared");
}

// ============================================================================
// ZPL PRINTING
// ============================================================================

/**
 * Print ZPL data to thermal printer
 * @param {Array<string>|string} zplData - ZPL command(s) to print
 * @param {string} printerName - Optional printer name (uses default if not provided)
 * @returns {Promise<void>}
 */
export async function printZpl(zplData, printerName = null) {
    log.info("Starting ZPL print job...");

    if (!isConnected()) {
        log.warn("Not connected, attempting to connect...");
        await connectToQZ();
    }

    let targetPrinter = printerName;
    if (!targetPrinter) {
        targetPrinter = getDefaultZplPrinter();
        log.debug("Using default ZPL printer:", targetPrinter);
    }

    if (!targetPrinter) {
        log.error("No printer specified and no default ZPL printer set");
        throw new Error("No ZPL printer configured. Please set a default printer.");
    }

    const foundPrinter = await findSpecificPrinter(targetPrinter);
    if (!foundPrinter) {
        log.error(`Printer "${targetPrinter}" not found`);
        throw new Error(QZ_ERRORS.PRINTER_NOT_FOUND + `: ${targetPrinter}`);
    }

    try {
        const config = qz.configs.create(foundPrinter);
        const dataArray = Array.isArray(zplData) ? zplData : [zplData];
        log.debug("Printing", dataArray.length, "ZPL label(s) to", foundPrinter);

        await withActiveQzOperation(() => qz.print(config, dataArray));

        log.success("✓ ZPL print job completed successfully!");
    } catch (error) {
        log.error("✗ ZPL print job failed");
        log.error(ERROR_DETAILS_MSG, error);
        log.error("Printer:", foundPrinter);
        log.error("Data:", Array.isArray(zplData) ? `${zplData.length} labels` : "Single label");
        throw new Error(QZ_ERRORS.PRINT_FAILED + ": " + error.message);
    }
}

// ============================================================================
// PDF PRINTING
// ============================================================================

// Standard shipping label sizes (in inches)
const LABEL_SIZES = {
    "4x6": { width: 4, height: 6 },
    "4x8": { width: 4, height: 8 },
    "4x4": { width: 4, height: 4 },
    "6x4": { width: 6, height: 4 },
};

/**
 * Safely get label dimensions by size
 * @param {string} labelSize - Label size key
 * @returns {Object} Dimensions object with width and height
 */
function getLabelDimensions(labelSize) {
    /* eslint-disable indent */
    switch (labelSize) {
        case "4x6":
            return LABEL_SIZES["4x6"];
        case "4x8":
            return LABEL_SIZES["4x8"];
        case "4x4":
            return LABEL_SIZES["4x4"];
        case "6x4":
            return LABEL_SIZES["6x4"];
        default:
            return LABEL_SIZES["4x6"];
    }
    /* eslint-enable indent */
}

/**
 * Print PDF to office printer
 * Supports bulk printing - pass an array of PDFs for optimized batch printing
 * @param {Blob|Blob[]|ArrayBuffer|ArrayBuffer[]|string|string[]} pdfData - PDF data (single or array)
 * @param {string} printerName - Optional printer name (uses default if not provided)
 * @param {Object} options - Print options (can include labelSize: '4x6', '4x8', etc.)
 * @returns {Promise<void>}
 */
export async function printPdf(pdfData, printerName = null, options = {}) {
    log.info("Starting PDF print job...");

    const pdfArray = Array.isArray(pdfData) ? pdfData : [pdfData];
    log.debug("PDF data type:", Array.isArray(pdfData) ? "Array" : pdfData.constructor.name);
    log.debug("Options:", options);

    if (!isConnected()) {
        log.warn("Not connected, attempting to connect...");
        await connectToQZ();
    }

    let targetPrinter = printerName;
    if (!targetPrinter) {
        targetPrinter = getDefaultPdfPrinter();
        log.debug("Using default PDF printer:", targetPrinter);
    }

    if (!targetPrinter) {
        log.error("No printer specified and no default PDF printer set");
        throw new Error("No PDF printer configured. Please set a default printer.");
    }

    const foundPrinter = await findSpecificPrinter(targetPrinter);
    if (!foundPrinter) {
        log.error(`Printer "${targetPrinter}" not found`);
        throw new Error(QZ_ERRORS.PRINTER_NOT_FOUND + `: ${targetPrinter}`);
    }

    try {
        log.debug(`Converting ${pdfArray.length} PDF(s) to base64...`);
        const base64Array = await Promise.all(
            pdfArray.map(async (pdf) => {
                if (pdf instanceof Blob) {
                    return await blobToBase64(pdf);
                } else if (pdf instanceof ArrayBuffer) {
                    return arrayBufferToBase64(pdf);
                } else if (typeof pdf === "string") {
                    return pdf; // Already base64
                } else {
                    throw new Error("Unsupported PDF data type: " + pdf.constructor.name);
                }
            })
        );

        log.debug("Base64 conversion complete");

        // Create print configuration for PDF
        const printOptions = {
            units: "in",
            margins: 0.03, // Small margin for printer safe zone (industry standard)
            density: 600, // 600 DPI for high-quality PDF rendering
            interpolation: "nearest-neighbor", // Prevents barcode blurring on label printers (per QZ docs)
            ...options, // Allow caller to override
        };

        // Apply label sizing when requested
        // IMPORTANT: We always set size when labelSize is provided, even if PDF is already correct size
        // This ensures QZ Tray knows the physical output dimensions (otherwise it may default to Letter)
        if (options.labelSize) {
            const dimensions = getLabelDimensions(options.labelSize);
            log.debug("Using specified label size:", options.labelSize, "Dimensions:", dimensions);
            printOptions.size = {
                width: dimensions.width,
                height: dimensions.height,
            };
        } else {
            // No label size specified - use PDF's natural size / printer's default
            log.debug("No label size specified, using PDF's natural dimensions with printer defaults");
        }

        const config = qz.configs.create(foundPrinter, printOptions);
        log.debug("Print config created:", printOptions);

        // Prepare all PDFs for QZ Tray (batch printing)
        const printData = base64Array.map((base64Data) => ({
            type: "pixel",
            format: "pdf",
            flavor: "base64",
            data: base64Data,
        }));

        await withActiveQzOperation(() => qz.print(config, printData));

        log.success(`✓ PDF print job completed successfully! (${pdfArray.length} document(s))`);
    } catch (error) {
        log.error("✗ PDF print job failed");
        log.error(ERROR_DETAILS_MSG, error);
        log.error("Printer:", foundPrinter);
        log.error("PDFs:", pdfArray.length);
        throw new Error(QZ_ERRORS.PRINT_FAILED + ": " + error.message);
    }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert Blob to base64 string
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result.split(",")[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Convert ArrayBuffer to base64 string
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
function arrayBufferToBase64(buffer) {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
        // eslint-disable-next-line security/detect-object-injection
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

/**
 * Check if QZ Tray is installed and running
 * @returns {Promise<boolean>}
 */
export async function isQzTrayRunning() {
    log.info("Checking if QZ Tray is running...");

    try {
        await connectToQZ({ retries: 1, retryDelay: 500 });
        log.success("✓ QZ Tray is running");
        return true;
    } catch (error) {
        log.warn("✗ QZ Tray is not running or not installed");
        return false;
    }
}

/**
 * Get installation instructions for QZ Tray
 * @returns {Object}
 */
export function getInstallationInstructions() {
    return {
        downloadUrl: "https://qz.io/download/",
        steps: [
            "1. Download QZ Tray from https://qz.io/download/",
            "2. Install the application on your computer",
            "3. Launch QZ Tray (it runs in your system tray)",
            "4. Refresh this page and try printing again",
        ],
        platforms: {
            windows: "Download QZ-Tray-Setup.exe",
            mac: "Download QZ-Tray.pkg",
            linux: "Download QZ-Tray.run or .deb package",
        },
    };
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize QZ Tray service
 * Attempts to connect automatically
 */
export async function initializeQzTray() {
    log.info("=== Initializing QZ Tray Service ===");

    try {
        await connectToQZ({ retries: 2, retryDelay: 1000 });

        // Load and verify saved printers
        const zplPrinter = getDefaultZplPrinter();
        const pdfPrinter = getDefaultPdfPrinter();

        log.info("Configuration:");
        log.info("  Default ZPL Printer:", zplPrinter || "Not set");
        log.info("  Default PDF Printer:", pdfPrinter || "Not set");

        if (zplPrinter || pdfPrinter) {
            log.info("Verifying saved printers...");
            const allPrinters = await findPrinters();

            if (zplPrinter && !allPrinters.some((p) => p.includes(zplPrinter))) {
                log.warn(`Saved ZPL printer "${zplPrinter}" not found`);
            }
            if (pdfPrinter && !allPrinters.some((p) => p.includes(pdfPrinter))) {
                log.warn(`Saved PDF printer "${pdfPrinter}" not found`);
            }
        }

        log.success("✓ QZ Tray Service initialized successfully");
        log.info("=== Initialization Complete ===");
    } catch (error) {
        log.error("✗ QZ Tray initialization failed");
        log.error("Error:", error.message);
        log.info("QZ Tray must be installed and running to use printing features");
        log.info("Download from: https://qz.io/download/");
    }
}

// Auto-initialize on module load (optional - can be called manually instead)
// Uncomment the next line to auto-initialize:
// initializeQzTray().catch(() => {});

const qzTrayServiceExports = {
    // Connection
    connectToQZ,
    disconnectFromQZ,
    isConnected,
    getConnectionStatus,

    // Printers
    findPrinters,
    findPrinterSummaries,
    findPrintersWithDetails,
    findSpecificPrinter,

    // Preferences
    getDefaultZplPrinter,
    setDefaultZplPrinter,
    clearDefaultZplPrinter,
    getDefaultPdfPrinter,
    setDefaultPdfPrinter,
    clearDefaultPdfPrinter,
    clearPrinterPreferences,

    // Printing
    printZpl,
    printPdf,

    // Utilities
    isQzTrayRunning,
    getInstallationInstructions,
    initializeQzTray,
};

export default qzTrayServiceExports;
